import { ReactElement, ReactNode } from 'react';
import { TextField, useField, useFieldArray } from '@ff-it/form';
import { Box, palette } from '@ff-it/ui';
import { BlockState, blockStates } from 'modules/campaign/block/types';
import { useDraggable, useDragMonitor, DragHandle } from 'components/DND';
import { AddInline, Inlines, RemoveInline } from 'components';
import { DropIndicator } from '@atlaskit/pragmatic-drag-and-drop-react-drop-indicator/box';
import { required } from 'utilities';
import { BlockStateHelpLabel } from 'modules/campaign/status/BlockStateHelpLabel';
import { BlockStatusTag } from 'modules/campaign/status/BlockStatusTag';

const parseName = (value: string) => {
  return value.toUpperCase();
};

function LabelPreview({ name, state }: { name: string; state: BlockState }): ReactElement {
  const {
    input: { value },
  } = useField(name, { subscription: { value: true } });

  return (
    <Box width="1/3" display="flex">
      <BlockStatusTag status={{ state, ...value }} appearance="naked" />
    </Box>
  );
}

function BlockStateRow({
  name,
  inlineName,
  index,
  disabled,
  state,
}: {
  name: string;
  inlineName: string;
  index: number;
  disabled: boolean;
  state: BlockState;
}): ReactElement {
  const { elementRef, handleRef, isDragging, closestEdge } = useDraggable(index, disabled, inlineName);

  return (
    <Box
      border="default"
      borderRadius="sm"
      display="flex"
      p="1"
      mb="1"
      gap="2"
      ref={elementRef}
      alignItems="center"
      style={{ position: 'relative', backgroundColor: isDragging ? palette.brand50 : undefined }}
    >
      {closestEdge && <DropIndicator edge={closestEdge} />}
      <DragHandle disabled={disabled} ref={handleRef} active={isDragging} />
      <LabelPreview name={name} state={state} />
      <TextField name={`${name}.name`} className="mb-0 col px-0" required validate={required} parse={parseName} />
      <RemoveInline name={inlineName} min={1} index={index} />
    </Box>
  );
}

const initialValues = (_idx: number) => ({
  name: '',
  __NEW__: Date.now(),
});

function BlockStateGroup({ state }: { state: BlockState }): ReactElement {
  const name = `statuses.${state}`;
  const { fields } = useFieldArray(name, { subscription: {} });

  useDragMonitor((sourceData, targetData, edge) => {
    if (edge === 'bottom') {
      fields.move(sourceData.index, targetData.index + 1);
    } else if (edge === 'top') {
      fields.move(sourceData.index, targetData.index);
    }
  }, name);
  return (
    <Box mb="3">
      <Box display="flex" alignItems="center" mb="1">
        <BlockStateHelpLabel>{state}</BlockStateHelpLabel>
        <AddInline name={name} initialValues={initialValues} className="ml-auto" testId={`add-${state}`} />
      </Box>
      <Inlines name={name}>{(props) => <BlockStateRow inlineName={name} state={state} {...props} />}</Inlines>
    </Box>
  );
}

export function Form({ children }: { children?: ReactNode }): ReactElement {
  return (
    <div className="container px-0 ml-0">
      <Box width="1/2" display="flex" flexDirection="column" mb="3">
        {children}
        <TextField name="name" label="Name" required validate={required} />
        {blockStates.map((state) => (
          <BlockStateGroup state={state} key={state} />
        ))}
      </Box>
    </div>
  );
}
